import { useContext, useEffect, useState } from "react";
import { ThemeContext } from "../../context/ThemeContext";
import lightLogo from "../home/logolong.png";
import darkLogo from "../home/OLDlogolong.png";
import styles from "./CategoryGuidelines.module.css";

export default function CategoryGuidelines() {
  const { isDarkMode } = useContext(ThemeContext);
  const [logo, setLogo] = useState(lightLogo);

  useEffect(() => {
    setLogo(isDarkMode ? darkLogo : lightLogo);
  }, [isDarkMode]);

  return (
    <>
      <header>
        <span></span>
        <img src={logo} alt="Linesol" style={{ height: "44px" }} />
        <span></span>
      </header>

      <div className={styles.pageContainer}>
        <div className={styles.contentWrapper}>
          <h1 className={styles.title}>User Features Guidelines</h1>
          <p className={styles.subtitle}>
            This guide provides comprehensive standards for defining and naming user categories and features,
            ensuring consistency, scalability, and proper governance across all studies.
          </p>

          <div className={styles.section}>
            <p className={styles.paragraph}>
              These standards exist to provide a scalable framework that gives clients complete freedom in selecting 
              the features they want to study, while ensuring the system remains maintainable and extensible over time. 
              The framework is designed so that any client can introduce new categories and features without disrupting 
              existing data or requiring users to re-select their profiles.
            </p>
            <p className={styles.paragraph}>
              Custom user features that are not covered by the standard library can be added on <a className={styles.link} href="https://dashboard.linesol.online/form2" target="_blank" rel="noopener noreferrer">dashboard.linesol.online/form2</a>.
            </p>
            <p className={styles.paragraph}>
              This scalability is achieved through three core principles: <strong>mutually exclusive categories</strong> that 
              prevent overlap and ambiguity, <strong>bounded feature ranges</strong> that can be subdivided for future 
              granularity without invalidating existing selections, and <strong>hierarchical nomenclature</strong> that 
              allows broad categories to spawn more specific variants for follow-up research. 
            </p>
          </div>

          <div className={styles.divider} />

          {/* ======================== NOMENCLATURE STANDARD ======================== */}
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>Nomenclature Standard</h2>

            <h3 className={styles.subSectionTitle}>Category Naming Structure</h3>
            <p className={styles.paragraph}>All categories must follow this format:</p>
            <div className={styles.codeBlock}>[Attribute] [Qualifier] [Measured Aspect] [Units] (width X, start Y)</div>

            <h4 className={styles.subSectionTitle}>Components:</h4>
            <ul className={styles.exampleList}>
              <li>• <strong>Attribute</strong> — The domain or thing being described. The attribute must be clear, specific, and self-contained so it is understandable without referring to feature labels. Examples: Salary, Age, Online Purchases, Exercise, TV Series, Personal Fitness.</li>
              <li>• <strong>Qualifier</strong> (optional) — Contextual qualifiers that clarify how the attribute is interpreted. Qualifiers must come after the attribute (post-modifiers), not before. If the category includes a measured aspect such as Goal or Motivation, place the qualifier before that aspect. Examples: Primary, After Tax, Average. Do not use pre-modifiers like "Preferred Language" or "Net Income", convert them instead to "Language Preference" or "Income After Tax". Where an average is specified, as a default the length of the averaging window is interpreted by the participant as 1 month for daily or weekly values or 1 year for monthly values. 
              </li>
              <li>• <strong>Measured Aspect</strong> (optional) — The head noun or noun phrase that names what is being measured within the attribute. This is not a unit. Examples: Preference, Goal, Motivation, Role, Context, Status.</li>
              <li>• <strong>Units</strong> (optional) — Measurement units that specify how the attribute is quantified. Examples: USD per Year, Years, Hours per Week, per Month (Do not use "/" — use "per" instead.)</li>
              <li>• <strong>Width</strong> (optional) — Size of each bin for numerical categories (e.g., width 25k, width 5). Omit if width = 1.</li>
              <li>• <strong>Start</strong> (optional) — Starting point of first bin (e.g., start 1, start 18). Omit if start = 0.</li>
              <li>• <strong>Brackets</strong> — Combine width and start in a single set of parentheses. Omit brackets entirely if both defaults apply (width 1, start 0).</li>
            </ul>

            <p className={styles.paragraph}>
              For labels such as <span className={styles.inlineCode}>Personal Fitness Primary Goal</span> or <span className={styles.inlineCode}>Home Cooking Primary Motivation</span>,
              <strong> Primary</strong> is the qualifier and <strong>Goal</strong> or <strong>Motivation</strong> is the measured aspect, not a unit.
            </p>

            <p className={styles.paragraph}><strong>Examples:</strong></p>
            <ul className={styles.exampleList}>
              <li>• <span className={styles.inlineCode}>Salary USD per Year (width 25k)</span> → Features: "0-&lt;25k", "25k-&lt;50k", "50k-&lt;75k" (start 0 omitted)</li>
              <li>• <span className={styles.inlineCode}>Age Years (width 10, start 18)</span> → Features: "18-27", "28-37", "38-47" (discrete, whole years)</li>
              <li>• <span className={styles.inlineCode}>Exercise Hours per Day (width 3)</span> → Features: "0-&lt;3", "3-&lt;6", "6-&lt;9" (start 0 omitted)</li>
              <li>• <span className={styles.inlineCode}>Online Purchases Average per Month (width 4, start 1)</span> → Features: "1-&lt;5", "6-&lt;9", "10-&lt;13"</li>
              <li>• <span className={styles.inlineCode}>TV Series Primary Viewing Goal</span> → Features: "Relax and unwind", "Feel suspense or tension", "Explore ideas and themes"</li>
              <li>• <span className={styles.inlineCode}>Dependents</span> → Features: "0", "1", "2", "3" (width 1, start 0 – no brackets needed)</li>
            </ul>

            <h3 className={styles.subSectionTitle}>Feature Naming Rules</h3>
            <ul className={styles.exampleList}>
              <li>• Use concise, readable formats.</li>
              <li>• Use compact magnitude prefixes where appropriate (e.g., k for thousands, M for millions).</li>
              <li>• Do not repeat units already included in the category name.</li>
            </ul>

            <h3 className={styles.subSectionTitle}>Continuous vs. Discrete Bins</h3>
            <ul className={styles.exampleList}>
              <li>• <strong>Continuous bins</strong> use half-open ranges (lower inclusive, upper exclusive): "0-&lt;2", "2-&lt;4", "4-&lt;6". Continuous bins are used when values may include decimals. Applies to: <strong>time</strong>, <strong>money</strong>, <strong>weight</strong>, <strong>averages</strong>.</li>
              <li>• <strong>Discrete bins</strong> (integer/cardinality-based) do <strong>not</strong> use half-open notation: "18-27", "28-37". Used when: <strong>age years</strong> (whole years is assumed), <strong>counts</strong> (number of purchases, dependents, visits), <strong>other</strong> integer-based categories.</li>
            </ul>

            <p className={styles.paragraph}><strong>Examples:</strong></p>
            <p className={styles.paragraph}>
              Category: <span className={styles.inlineCode}>Salary USD per Year (width 50k)</span>
            </p>
            <p>
              <span className={styles.bad}>❌</span> Features: "0-&lt;50k USD/Year", "50k-&lt;100k USD/Year" (redundant)
            </p>
            <p>
              <span className={styles.good}>✅</span> Features: "0-&lt;50k", "50k-&lt;100k", "100k-&lt;150k" (continuous, "k" = thousands)
            </p>

            <p className={styles.paragraph} style={{ marginTop: "12px" }}>
              Category: <span className={styles.inlineCode}>Age Years (width 10, start 18)</span>
            </p>
            <p>
              <span className={styles.bad}>❌</span> Features: "18-27 years", "28-37 years" (redundant)
            </p>
            <p>
              <span className={styles.good}>✅</span> Features: "18-27", "28-37", "38-47" (discrete, whole years)
            </p>

            <h3 className={styles.subSectionTitle}>Non-Numerical Categories</h3>
            <p className={styles.paragraph}>
              For categories without numerical ranges, the category name alone must clearly identify the measured attribute
              and include any necessary contextual qualifier or measured aspect. Width and start parameters must be omitted.
            </p>
            <div className={styles.codeBlock}>[Attribute] [Qualifier] [Measured Aspect] [Units]</div>
            <p className={styles.paragraph}><strong>Examples:</strong></p>
            <ul className={styles.exampleList}>
              <li>• <span className={styles.inlineCode}>Industry</span></li>
              <li>• <span className={styles.inlineCode}>Country of Residence</span></li>
              <li>• <span className={styles.inlineCode}>Diet Type</span></li>
              <li>• <span className={styles.inlineCode}>Employment Status</span></li>
              <li>• <span className={styles.inlineCode}>Language Preference</span></li>
              <li>• <span className={styles.inlineCode}>TV Series Primary Viewing Goal</span></li>
            </ul>
          </div>

          <div className={styles.divider} />

          {/* ======================== GOVERNANCE RULES ======================== */}
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>Governance Rules</h2>

            {/* Rule 1 */}
            <div className={styles.ruleCard}>
              <p className={styles.ruleTitle}>Rule 1: Single-Select Per Category</p>
              <p className={styles.paragraph}>
                <strong>Principle:</strong> Each participant selects exactly ONE feature per category.
              </p>
              <p className={styles.paragraph}>
                <strong>Implication:</strong> Features within a category must be mutually exclusive.
              </p>
              <p className={styles.paragraph}>
                <strong>Validation:</strong> If two features seem like "both could apply", they belong in different categories.
              </p>
            </div>

            {/* Rule 2 */}
            <div className={styles.ruleCard}>
              <p className={styles.ruleTitle}>Rule 2: No Negation Features in Groups</p>
              <p className={styles.paragraph}>
                <strong>Prohibition:</strong> Negations should be defined as separate yes/no categories.
              </p>
              <p className={styles.paragraph}>
                <strong>Rationale:</strong> To avoid features overlapping within categories.
              </p>
              <p className={styles.paragraph}>
                <strong>Implication:</strong> There is currently no implementation for a user to be auto-selected for their feature
                based on a parent category. They will have to manually select each category.
              </p>
              <p className={styles.paragraph}><span className={styles.bad}>❌ BAD:</span></p>
              <div className={styles.jsonBlock}>
{`{"categoryName": "Industry", "featuresName": "Technology"}
{"categoryName": "Industry", "featuresName": "Finance"}
{"categoryName": "Industry", "featuresName": "Healthcare"}
{"categoryName": "Industry", "featuresName": "Non-tech"}`}
              </div>
              <p className={styles.paragraph}><span className={styles.good}>✅ GOOD:</span> Use non-overlapping features instead.</p>
              <div className={styles.jsonBlock}>
{`{"categoryName": "Industry Technology", "featuresName": "Yes"}
{"categoryName": "Industry Technology", "featuresName": "No"}`}
              </div>
            </div>

            {/* Rule 3 */}
            <div className={styles.ruleCard}>
              <p className={styles.ruleTitle}>Rule 3: No Catch-All Features</p>
              <p className={styles.paragraph}>
                <strong>Prohibition:</strong> Features must NEVER be catch-all bins like "Other", "Traditional",
                "Miscellaneous", "General", or open-ended ranges like "55+", "200k+", "12+".
              </p>
              <p className={styles.paragraph}>
                <strong>Rationale:</strong> Catch-alls prevent future granularity and scalability. If a client wants to study
                the 300k–400k income segment but you already have "300k+", users earning 300k–500k are trapped in that
                bin and cannot be re-segmented for granular analysis.
              </p>
              <p className={styles.paragraph}>
                <strong>Scalability Benefit:</strong> Specific bounded ranges allow future studies to add more granular segments
                without invalidating existing user selections. Participants who cannot select an existing option can choose
                "Rather not say" to opt out temporarily until a valid feature exists.
              </p>
              <p className={styles.paragraph}><span className={styles.bad}>❌ BAD:</span></p>
              <div className={styles.jsonBlock}>
{`{"categoryName": "Work Sector", "featuresName": "Traditional Industry"}
{"categoryName": "Income", "featuresName": "200k+"}
{"categoryName": "Age", "featuresName": "55+"}`}
              </div>
              <p className={styles.paragraph}><span className={styles.good}>✅ GOOD:</span> Use specific, atomic features that don't subsume others.</p>
              <div className={styles.jsonBlock}>
{`{"categoryName": "Industry", "featuresName": "Technology"}
{"categoryName": "Industry", "featuresName": "Finance"}
{"categoryName": "Industry", "featuresName": "Healthcare"}
{"categoryName": "Industry", "featuresName": "Retail"}`}
              </div>
            </div>

            {/* Rule 4 */}
            <div className={styles.ruleCard}>
              <p className={styles.ruleTitle}>Rule 4: Mutually Exclusive Numerical Ranges</p>
              <p className={styles.paragraph}>
                <strong>Requirement:</strong> Numerical ranges must have NO overlapping boundaries.
              </p>
              <p className={styles.paragraph}>
                <strong>Rationale:</strong> Overlapping ranges create confusion. Someone earning exactly $100k shouldn't have to
                guess between "75k-100k" and "100k-150k".
              </p>
              <p className={styles.paragraph}>
                <strong>Pattern:</strong> For continuous bins, use half-open ranges like "75k-&lt;100k", "100k-&lt;150k", "150k-&lt;200k".
              </p>
              <p className={styles.paragraph}><span className={styles.bad}>❌ BAD:</span></p>
              <div className={styles.jsonBlock}>
{`{"categoryName": "Salary USD per Year (width 50k)", "featuresName": "50k-100k"}
{"categoryName": "Salary USD per Year (width 50k)", "featuresName": "100k-150k"}`}
              </div>
              <p className={styles.paragraph}><span className={styles.good}>✅ GOOD:</span></p>
              <div className={styles.jsonBlock}>
{`{"categoryName": "Salary USD per Year (width 50k)", "featuresName": "50k-<100k"}
{"categoryName": "Salary USD per Year (width 50k)", "featuresName": "100k-<150k"}`}
              </div>
            </div>

            {/* Rule 5 */}
            <div className={styles.ruleCard}>
              <p className={styles.ruleTitle}>Rule 5: Consistent Bin Sizes</p>
              <p className={styles.paragraph}>
                <strong>Requirement:</strong> Within each category, all numerical ranges should use consistent bin sizes.
              </p>
              <p className={styles.paragraph}>
                <strong>Rationale:</strong> Inconsistent bin sizes create confusion.
              </p>
              <p className={styles.paragraph}>
                <strong>Application:</strong> If inconsistent bin sizes are required in the study, leverage different category names,
                but avoid overlap due to multicollinearity.
              </p>
              <p className={styles.paragraph}><span className={styles.bad}>❌ BAD:</span></p>
              <div className={styles.jsonBlock}>
{`{"categoryName": "Salary USD per Year", "featuresName": "0-<25k"}
{"categoryName": "Salary USD per Year", "featuresName": "25k-<100k"}
{"categoryName": "Salary USD per Year", "featuresName": "100k-<150k"}`}
              </div>
              <p className={styles.paragraph}><span className={styles.good}>✅ GOOD:</span></p>
              <div className={styles.jsonBlock}>
{`{"categoryName": "Salary USD per Year (width 25k)", "featuresName": "0-<25k"}
{"categoryName": "Salary USD per Year (width 25k)", "featuresName": "25k-<50k"}
{"categoryName": "Salary USD per Year (width 50k)", "featuresName": "0-<50k"}
{"categoryName": "Salary USD per Year (width 50k)", "featuresName": "50k-<100k"}
{"categoryName": "Salary USD per Year (width 50k)", "featuresName": "100k-<150k"}
{"categoryName": "Salary USD per Year (width 50k)", "featuresName": "150k-<200k"}`}
              </div>
            </div>

            {/* Rule 6 */}
            <div className={styles.ruleCard}>
              <p className={styles.ruleTitle}>Rule 6: Use Measurable or Specific Definitions</p>
              <p className={styles.paragraph}>
                <strong>Requirement:</strong> When an attribute can be measured, define it with explicit units and bounded values or ranges instead of subjective labels. When an attribute is not naturally numeric, use specific, atomic categories.
              </p>
              <p className={styles.paragraph}>
                <strong>Rationale:</strong> The goal is not to force every feature into numbers. The goal is to avoid vague labels such as "high," "medium," "low," or "frequent" that different people may interpret differently. Measurable ranges improve consistency for numeric attributes, while specific atomic labels preserve clarity for non-numeric attributes.
              </p>
              <p className={styles.paragraph}>
                When a user feature represents an average, treat it as continuous and use half-open bins, even if the underlying quantity is a count. The implied averaging window is 1 month for daily or weekly values, and 1 year for monthly values.
              </p>
              <p className={styles.paragraph}>
                Non-numerical attributes are also valid when they use specific, atomic categories such as "Industry," "Diet Type," or "Employment Status."
              </p>
              <p className={styles.paragraph}><span className={styles.bad}>❌ BAD:</span></p>
              <div className={styles.jsonBlock}>
{`{"categoryName": "Purchasing Behavior", "featuresName": "Frequent"}`}
              </div>
              <p className={styles.paragraph}><span className={styles.good}>✅ GOOD:</span></p>
              <div className={styles.jsonBlock}>
{`{"categoryName": "Online Purchases Average per Month (width 4)", "featuresName": "0-<4"}
{"categoryName": "Online Purchases Average per Month (width 4)", "featuresName": "4-<8"}
{"categoryName": "Online Purchases Average per Month (width 4)", "featuresName": "8-<12"}`}
              </div>


            </div>

            {/* Rule 7 */}
            <div className={styles.ruleCard}>
              <p className={styles.ruleTitle}>Rule 7: Be Specific About What's Measured</p>
              <p className={styles.paragraph}>
                <strong>Requirement:</strong> Category names must clearly indicate the attribute being measured and, where applicable, include the unit or time basis.
              </p>
              <p className={styles.paragraph}>
                <strong>Rationale:</strong> "Purchasing Behavior" is vague because it does not say what is being measured, in what context, or over what time period.
              </p>
              <p className={styles.paragraph}><span className={styles.bad}>❌ BAD:</span> "Purchasing Behavior", "Activity Level", "Engagement"</p>
              <p className={styles.paragraph}><span className={styles.good}>✅ GOOD:</span> "Online Purchases per Month (width 4)", "Exercise Hours per Week (width 3)", "Social Media Hours per Day (width 2)"</p>
            </div>

            {/* Rule 8 */}
            <div className={styles.ruleCard}>
              <p className={styles.ruleTitle}>Rule 8: Context-Independent Naming</p>
              <p className={styles.paragraph}>
                <strong>Requirement:</strong> Category and feature names must be self-explanatory without study context.
              </p>
              <p className={styles.paragraph}>
                <strong>Rationale:</strong> Users don't see the study question when selecting their profile; names must stand alone.
              </p>
              <p className={styles.paragraph}>
                <strong>Conciseness:</strong> No limit on length, but keep as short as possible while maintaining clarity.
              </p>
            </div>

            {/* Rule 9 */}
            <div className={styles.ruleCard}>
              <p className={styles.ruleTitle}>Rule 9: Hierarchy Through Nomenclature</p>
              <p className={styles.paragraph}>
                <strong>Principle:</strong> Start with general categories, then create more granular versions for follow-up studies.
              </p>
              <p className={styles.paragraph}>
                <strong>Pattern:</strong> Use suffixes to indicate granularity level.
              </p>
              <p className={styles.paragraph}><strong>Example Hierarchy:</strong></p>
              <ul className={styles.exampleList}>
                <li>• <span className={styles.inlineCode}>Social Media Hours per Day (width 2)</span> = "0-&lt;2", "2-&lt;4", "4-&lt;6", etc. (broad)</li>
                <li>• <span className={styles.inlineCode}>Social Media Instagram Hours per Day (width 2)</span> = "0-&lt;2", "2-&lt;4", "4-&lt;6", etc. (fine granularity for follow-up)</li>
              </ul>
              <p className={styles.paragraph}>
                <strong>Workflow:</strong> Client sees high importance for "Social Media Hours per Day (width 2): 4-&lt;6" in results → submits follow-up study with platform-specific categories to drill down to which social media site is most influential.
              </p>
            </div>

            {/* Rule 10 */}
            <div className={styles.ruleCard}>
              <p className={styles.ruleTitle}>Rule 10: Extensibility Without Conflict</p>
              <p className={styles.paragraph}>
                <strong>Principle:</strong> New features can be added to existing categories by any client without invalidating existing data.
              </p>
              <p className={styles.paragraph}>
                <strong>Test:</strong> Ask "If I add this feature, would existing users need to re-select?" If yes, create a new category instead.
              </p>
              <p className={styles.paragraph}><span className={styles.bad}>❌ BAD:</span> <span className={styles.inlineCode}>Work Sector</span> with "Technology", "Traditional Industry" cannot accept "Agriculture" cleanly.</p>
              <p className={styles.paragraph}><span className={styles.good}>✅ GOOD:</span> <span className={styles.inlineCode}>Industry</span> with "Technology", "Finance" can later accept "Agriculture", "Education" without conflict.</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
